import React, { useEffect, useRef, useState } from "react";
import { useToast } from "@chakra-ui/react";
import {
  FaMapMarkerAlt,
  FaYoutube,
  FaInstagram,
  FaTiktok,
  FaInfoCircle,
} from "react-icons/fa";
import "./style.css";
import { Link } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_DEPLOYED_BASE_URL;
const OFFER_POST_URL = BASE_URL + "/send-offer/";

const formatNumber = (num) => {
  if (num == null) return "-";
  const n = Number(num); // ensures it's a number

  if (n === 0 || isNaN(n)) return "-";

  if (num >= 1_000_000) {
    const formatted = (num / 1_000_000).toFixed(1);
    return `${parseFloat(formatted)}M`; // Removes trailing .0
  } else if (num >= 1_000) {
    const formatted = (num / 1_000).toFixed(2);
    return `${parseFloat(formatted)}K`; // Removes trailing .0 or .00
  } else {
    return parseFloat(num.toString()).toString(); // Cleans .0 as well
  }
};

// OfferModal Component
const OfferModal = ({ open, onClose, onSubmit, creatorName, creator }) => {
  const formContainer = useRef(null);
  const [form, setForm] = useState({
    talent_name: creatorName,
    first_name: "",
    last_name: "",
    brand_name: "",
    reps_email: "",
    deliverables: "",
    offer: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) {
      setForm({
        talent_name: creatorName,
        first_name: "",
        last_name: "",
        brand_name: "",
        reps_email: "",
        deliverables: "",
        offer: "",
      });
      setErrors({});
    }
  }, [open, creatorName]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        formContainer.current &&
        !formContainer.current.contains(event.target)
      ) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [open, onClose]);

  const validate = () => {
    const newErrors = {};
    if (!form.first_name.trim()) newErrors.first_name = "Required";
    if (!form.last_name.trim()) newErrors.last_name = "Required";
    if (!form.brand_name.trim()) newErrors.brand_name = "Required";
    if (!form.reps_email.trim()) newErrors.reps_email = "Required";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.reps_email))
      newErrors.reps_email = "Invalid email";
    if (!form.deliverables.trim()) newErrors.deliverables = "Required";
    if (!form.offer.toString().trim()) newErrors.offer = "Required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(form, creator);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="offer-modal">
      <div className="offer-modal-content" ref={formContainer}>
        <button className="offer-close-button" onClick={onClose}>
          &times;
        </button>
        <h2 style={{ marginBottom: "1rem" }}>
          {" "}
          {creatorName ? `Send Offer to ${creatorName}` : "Contact Us"}{" "}
        </h2>
        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}
        >
          <div className="offer-modal-header">
            <label>First Name*</label>
            <br />
            <input
              className="offer-input"
              name="first_name"
              value={form.first_name}
              onChange={handleChange}
              s
            />
            {errors.first_name && (
              <div style={{ color: "red", fontSize: 12 }}>
                {errors.first_name}
              </div>
            )}
          </div>
          <div className="offer-modal-header">
            <label>Last Name*</label>
            <br />
            <input
              className="offer-input"
              name="last_name"
              value={form.last_name}
              onChange={handleChange}
            />
            {errors.last_name && (
              <div style={{ color: "red", fontSize: 12 }}>
                {errors.last_name}
              </div>
            )}
          </div>
          <div className="offer-modal-header">
            <label>Brand Name*</label>
            <br />
            <input
              className="offer-input"
              name="brand_name"
              value={form.brand_name}
              onChange={handleChange}
            />
            {errors.brand_name && (
              <div style={{ color: "red", fontSize: 12 }}>
                {errors.brand_name}
              </div>
            )}
          </div>
          <div className="offer-modal-header">
            <label>Rep's Email*</label>
            <br />
            <input
              className="offer-input"
              name="reps_email"
              value={form.reps_email}
              onChange={handleChange}
            />
            {errors.reps_email && (
              <div style={{ color: "red", fontSize: 12 }}>
                {errors.reps_email}
              </div>
            )}
          </div>
          <div className="offer-modal-header">
            <label>Deliverables*</label>
            <br />
            <textarea
              className="offer-input"
              name="deliverables"
              value={form.deliverables}
              onChange={handleChange}
            />
            {errors.deliverables && (
              <div style={{ color: "red", fontSize: 12 }}>
                {errors.deliverables}
              </div>
            )}
          </div>
          <div className="offer-modal-header">
            <label>{creatorName ? `Offer*` : "Budget*"}</label>
            <br />
            <input
              className="offer-input"
              type="number"
              name="offer"
              value={form.offer}
              onChange={handleChange}
            />
            {errors.offer && (
              <div style={{ color: "red", fontSize: 12 }}>{errors.offer}</div>
            )}
          </div>
          <div style={{ flexBasis: "100%" }}></div>
          <button className="offer-submit-button" type="submit">
            Send Offer
          </button>
        </form>
      </div>
    </div>
  );
};

const CreatorCard = ({ creator, handleOfferSubmit }) => {
  const [activeInfo, setActiveInfo] = useState(null);
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [activeCardIndex, setActiveCardIndex] = useState(null);

  const creatorStats = [
    {
      icon: <FaYoutube style={{ fontSize: "20px", color: "#FF0000" }} />,
      count: formatNumber(creator?.yt_subscriber_count),
      url: creator?.youtube_url,
      info: {
        us: formatNumber(creator?.yt_us_percentage),
        male: formatNumber(creator?.yt_male_percentage),
        female: formatNumber(creator?.yt_female_percentage),
      },
    },
    {
      icon: <FaInstagram style={{ fontSize: "20px", color: "#E1306C" }} />,
      count: formatNumber(creator?.ig_followers_count),
      url: creator?.instagram_url,
      info: {
        us: formatNumber(creator?.ig_us_percentage),
        male: formatNumber(creator?.ig_male_percentage),
        female: formatNumber(creator?.ig_female_percentage),
      },
    },
    {
      icon: <FaTiktok style={{ fontSize: "20px", color: "#000" }} />,
      count: formatNumber(creator?.tk_followers_count),
      url: creator?.tiktok_url,
      info: {
        us: formatNumber(creator?.tk_us_percentage),
        male: formatNumber(creator?.tk_male_percentage),
        female: formatNumber(creator?.tk_female_percentage),
      },
    },
  ];

  return (
    <div className="row_card_outer">
      <div className="list_outer">
        <div
          className="mobile_card_design"
          style={{ display: activeCardIndex === 0 ? "block" : "none" }}
        >
          <div className="profile_box">
            <div className="dp_box">
              <img src={creator.profile_picture} alt={creator.talent_name} />
            </div>
            <div
              className="for_mobile pro_info_icon"
              onClick={() =>
                setActiveCardIndex(activeCardIndex === 0 ? null : 0)
              }
            >
              <FaInfoCircle
                style={{
                  fontSize: "19px",
                }}
              />
            </div>
            <div className="profile_desc">
              <h2
                style={{
                  fontSize: "1.125rem",
                  fontWeight: "700",
                }}
              >
                {creator.talent_name}
              </h2>

              <p
                style={{
                  fontSize: "0.875rem",
                  color: "#9ca3af",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FaMapMarkerAlt
                  style={{
                    color: "grey",
                    fontSize: "1rem",
                    marginRight: "4px",
                  }}
                />
                {creator.country}
              </p>
            </div>
          </div>
          <div
            className="media_tags"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.25rem",
              marginTop: "0.25rem",
            }}
          >
            {creator?.categories?.map((tag, idx) => (
              <span className="card_tags" key={idx}>
                {tag}
              </span>
            ))}
          </div>
          <div
            style={{
              backgroundColor: "#000",
              color: "#fff",
              padding: "1rem",
              marginTop: "1rem",
              borderRadius: "8px",
            }}
          >
            {/* YouTube Section */}
            <div style={{ marginBottom: "1rem" }}>
              <h3 style={{ fontWeight: "bold" }}>YouTube</h3>
              <p>{formatNumber(creator.yt_subscriber_count)} Subscribers</p>
              <p>{formatNumber(creator.yt_us_percentage)}% USA</p>
              <p>
                {formatNumber(creator.yt_female_percentage)}% Female /{" "}
                {formatNumber(creator.yt_male_percentage)}% Male
              </p>
            </div>

            {/* Instagram Section */}
            <div style={{ marginBottom: "1rem" }}>
              <h3 style={{ fontWeight: "bold" }}>Instagram</h3>
              <p>{formatNumber(creator.ig_followers_count)} Followers</p>
              <p>{formatNumber(creator.ig_us_percentage)}% USA</p>
              <p>
                {formatNumber(creator.ig_female_percentage)}% Female /{" "}
                {formatNumber(creator.ig_male_percentage)}% Male
              </p>
            </div>

            {/* TikTok Section */}
            <div style={{ marginBottom: "1rem" }}>
              <h3 style={{ fontWeight: "bold" }}>TikTok</h3>
              <p>{formatNumber(creator.tk_followers_count)} Followers</p>
              <p>{formatNumber(creator.tk_us_percentage)}% USA</p>
              <p>
                {formatNumber(creator.tk_female_percentage)}% Female /{" "}
                {formatNumber(creator.tk_male_percentage)}% Male
              </p>
            </div>
          </div>
          {/* Offer Button */}
          <div className="offer_btn_info_icon">
            <button
              className="custom_btn"
              onMouseOver={(e) => {
                e.target.style.backgroundColor = "#3278bf";
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = "#3278bf";
              }}
              onClick={() => setOfferModalOpen(true)}
            >
              Offer
            </button>
          </div>
        </div>
        <div className="profile_box">
          <div className="dp_box">
            <img src={creator.profile_picture} alt={creator.talent_name} />
            <div
              className="for_mobile pro_info_icon"
              onClick={() =>
                setActiveCardIndex(activeCardIndex === 0 ? null : 0)
              }
            >
              <FaInfoCircle
                style={{
                  fontSize: "19px",
                }}
              />
            </div>
          </div>
          <div className="profile_desc">
            <h2
              style={{
                fontSize: "1.125rem",
                fontWeight: "700",
              }}
            >
              {creator.talent_name}
            </h2>

            <p
              style={{
                fontSize: "0.875rem",
                color: "#9ca3af",
                display: "flex",
                alignItems: "center",
              }}
            >
              <FaMapMarkerAlt
                style={{ color: "grey", fontSize: "1rem", marginRight: "4px" }}
              />
              {creator.country}
            </p>
            <div className="media_tags">
              {creator?.categories?.map((tag, idx) => (
                <span className="card_tags" key={idx}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="row_card_box">
          {creatorStats
            .filter(
              (platform) =>
                platform.count !== "N/A" &&
                platform.count !== "0" &&
                platform.count !== "-"
            )
            .map((platform, idx) => (
              <div key={idx} className="media_card">
                <div className="media_type">
                  {platform.url ? (
                    <a
                      href={platform.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      {platform.icon}
                    </a>
                  ) : (
                    platform.icon
                  )}
                </div>
                <div className="follower_number">
                  <h3>{platform.count}</h3>
                </div>

                <div className="media_info">
                  {" "}
                  <FaInfoCircle
                    style={{
                      fontSize: "14px",
                      color: "#9ca3af",
                      cursor: "pointer",
                      marginTop: "4px",
                    }}
                    onClick={() =>
                      setActiveInfo(activeInfo === idx ? null : idx)
                    }
                  />
                </div>
                {/* Info Box */}
                {activeInfo === idx && (
                  <div className="hover_box">
                    <div className="media_info">
                      {" "}
                      <FaInfoCircle
                        style={{
                          fontSize: "14px",
                          color: "#9ca3af",
                          cursor: "pointer",
                          marginTop: "4px",
                        }}
                        onClick={() =>
                          setActiveInfo(activeInfo === idx ? null : idx)
                        }
                      />
                    </div>
                    <p>USA: {platform.info.us || "N/A"}%</p>
                    <p>Male: {platform.info.male || "N/A"}%</p>
                    <p>Female: {platform.info.female || "N/A"}%</p>
                  </div>
                )}
              </div>
            ))}
        </div>

        <div className="offer_btn">
          <button
            className="custom_btn"
            onMouseOver={(e) => {
              e.target.style.backgroundColor = "#3278bf";
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = "#3278bf";
            }}
            onClick={() => setOfferModalOpen(true)}
          >
            Offer
          </button>
          <OfferModal
            open={offerModalOpen}
            onClose={() => setOfferModalOpen(false)}
            onSubmit={handleOfferSubmit}
            creatorName={creator.talent_name}
            creator={creator}
          />
        </div>
      </div>
    </div>
  );
};

const Sidebar = ({ stats, handleOfferSubmit }) => {
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  return (
    <div className="left-sidebar">
      <div className="side_bar_inner">
        <div className="left-sidebar-content">
          <div className="logo_part">
            <img
              src="/cm_logo.png"
              alt="Logo"
              style={{ width: "100%", maxwidth: "160px", marginBottom: "1rem" }}
            />
          </div>
          <div className="creators_outer">
            <div className="creators_count">
              <p className="sidebar-heading">Total Creators</p>
              <h2 className="sidebar-h">{stats.totalCreators}</h2>
            </div>
            <div className="creators_count">
              <p className="sidebar-heading">Total Reach</p>
              <h2 className="sidebar-h">{formatNumber(stats.totalReach)}</h2>
            </div>
          </div>
          <div className="social-stats">
            <div className="social-stats-data">
              <FaYoutube style={{ fontSize: "35px", color: "#f30b0b" }} />
              <p className="social-stats-text">{formatNumber(stats.youtube)}</p>
            </div>

            <div className="social-stats-data">
              <FaInstagram
                style={{ fontSize: "35px", color: "rgb(243 27 101)" }}
              />
              <p className="social-stats-text">
                {formatNumber(stats.instagram)}
              </p>
            </div>

            <div className="social-stats-data">
              <FaTiktok style={{ fontSize: "35px", color: "#000000" }} />
              <p className="social-stats-text">{formatNumber(stats.tiktok)}</p>
            </div>
          </div>
        </div>

        <button
          className="contact-button"
          onClick={() => setOfferModalOpen(true)}
        >
          Contact Us
        </button>
        <OfferModal
          open={offerModalOpen}
          onClose={() => setOfferModalOpen(false)}
          onSubmit={handleOfferSubmit}
          creatorName={""}
          creator={{}}
        />
      </div>
    </div>
  );
};

const CreatorList = () => {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metaStats, setMetaStats] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [minReach, setMinReach] = useState("");
  const [maxReach, setMaxReach] = useState("");
  const [minUsa, setMinUsa] = useState("");
  const [maxUsa, setMaxUsa] = useState("");
  const toast = useToast();
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState([]);
  const [minFemale, setminFemale] = useState("");
  const [maxFemale, setmaxFemale] = useState("");
  const [minMale, setminMale] = useState("");
  const [maxMale, setmaxMale] = useState("");

  const SITE_KEY = "6LfvHEArAAAAAEKbWYh54PMdI4zzWEgSsP4p3-g9";

  const fetchCountries = async () => {
    try {
      const response = await fetch(
        "https://restcountries.com/v3.1/all/?fields=name",
        {
          headers: {
            Accept: "application/json",
          },
        }
      );
      const data = await response.json();
      const countryNames = data.map(
        (country) => country.name.nativeName.eng?.common || country.name.common
      );
      setCountries(countryNames.sort());
    } catch (error) {
      console.error("Error fetching countries:", error);
      setCountries([
        "India",
        "United States",
        "United Kingdom",
        "Canada",
        "Germany",
        "France",
        "Australia",
        "Brazil",
        "Japan",
        "Other",
      ]);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.id = "recaptcha-script";
    document.body.appendChild(script);

    return () => {
      const scriptTag = document.getElementById("recaptcha-script");
      if (scriptTag) {
        document.body.removeChild(scriptTag);
      }
      const badge = document.querySelector(".grecaptcha-badge");
      if (badge) {
        badge.parentNode.removeChild(badge);
      }

      if (window.grecaptcha) {
        delete window.grecaptcha;
      }
    };
  }, []);

  const fetchCreators = async (
    search = "",
    categories = [],
    min = "",
    max = "",
    us_min = "",
    us_max = "",
    selectedCountry = "",
    selectedPlatform = [],
    minFemale = "",
    maxFemale = "",
    minMale = "",
    maxMale = ""
  ) => {
    try {
      const query = new URLSearchParams();
      if (search) query.append("search", search);
      if (categories.length > 0) {
        categories.forEach((cat) => query.append("categories", cat));
      }
      if (min) query.append("min_reach", min);
      if (max) query.append("max_reach", max);
      if (us_min) query.append("us_min", us_min);
      if (us_max) query.append("us_max", us_max);
      console.log("Selected Platform:", selectedPlatform);
      console.log("Selected cntyr:", selectedCountry);
      if (selectedCountry) query.append("search", selectedCountry);
      if (selectedPlatform && selectedPlatform.length > 0) {
        selectedPlatform.forEach((platform) =>
          query.append("platforms", platform)
        );
      }
      if (minFemale) query.append("min_female", minFemale);
      if (maxFemale) query.append("max_female", maxFemale);
      if (minMale) query.append("min_male", minMale);
      if (maxMale) query.append("max_male", maxMale);

      const response = await fetch(
        `${BASE_URL}/cm-roster-inf/?${query.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      console.log(data);

      if (data && data.data) {
        setCreators(data.data);
        setMetaStats({
          totalCreators: data.total_creator,
          totalReach: data.total_reach,
          youtube: data.platform_stats.youtube,
          instagram: data.platform_stats.instagram,
          tiktok: data.platform_stats.tiktok,
        });
      }
    } catch (error) {
      console.error("Error fetching creators:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCreators();
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchCreators(
        searchText,
        selectedCategories,
        minReach,
        maxReach,
        minUsa,
        maxUsa,
        selectedCountry,
        selectedPlatform,
        minFemale,
        maxFemale,
        minMale,
        maxMale
      );
    }, 500); // 500ms delay

    return () => clearTimeout(delayDebounce);
  }, [
    searchText,
    selectedCategories,
    minReach,
    maxReach,
    minUsa,
    maxUsa,
    selectedCountry,
    selectedPlatform,
    minFemale,
    maxFemale,
    minMale,
    maxMale,
  ]);

  if (loading) {
    return (
      <div className="text-center py-10">
        <div className="loader"></div>
      </div>
    );
  }

  // --csv--------------

  const handleExportCSV = async (
    search = "",
    categories = [],
    min = "",
    max = "",
    us_min = "",
    us_max = "",
    selectedCountry = "",
    selectedPlatform = [],
    minFemale = "",
    maxFemale = "",
    minMale = "",
    maxMale = ""
  ) => {
    try {
      const query = new URLSearchParams();
      if (search) query.append("search", search);
      console.log(search, "=======");
      if (categories.length > 0) {
        categories.forEach((cat) => query.append("categories", cat));
      }
      if (min) query.append("min_reach", min);
      if (max) query.append("max_reach", max);
      if (us_min) query.append("us_min", us_min);
      if (us_max) query.append("us_max", us_max);
      if (selectedCountry) query.append("search", selectedCountry);
      if (selectedPlatform && selectedPlatform.length > 0) {
        selectedPlatform.forEach((platform) =>
          query.append("platforms", platform)
        );
      }
      if (minFemale) query.append("min_female", minFemale);
      if (maxFemale) query.append("max_female", maxFemale);
      if (minMale) query.append("min_male", minMale);
      if (maxMale) query.append("max_male", maxMale);

      const response = await fetch(
        `${BASE_URL}/roster-csv/?${query.toString()}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) throw new Error("Failed to download CSV");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `INF_List_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  const toggleSidebar = () => {
    document.body.classList.toggle("side_menu_open");
  };

  const handleOfferSubmit = async (formData, creator) => {
    try {
      const token = await window.grecaptcha.execute(SITE_KEY, {
        action: "form_submit",
      });

      const response = await fetch(OFFER_POST_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...formData, creatorId: creator?.id, token }),
      });
      if (!response.ok) {
        throw new Error("Failed to send offer");
      }
      toast({
        title: "Offer Submitted",
        description: "Your offer has been successfully submitted.",
        status: "success",
        position: "top-right",
        duration: 3000,
        isClosable: true,
      });
      console.log("Offer submitted for", creator?.talent_name, formData);
    } catch (error) {
      toast({
        title: "Offer Submitted",
        description:
          "There was an error submitting your offer. Please try again.",
        status: "error",
        position: "top-right",
        duration: 3000,
        isClosable: true,
      });
      console.error("Error submitting offer:", error);
    }
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setSelectedCategories(
      (prev) =>
        prev.includes(value)
          ? prev.filter((cat) => cat !== value) // remove
          : [...prev, value] // add
    );
  };

  const iconSize = "30px";
  const unselectedBg = "#e5e7eb"; // Light gray
  const unselectedColor = "#717274";
  const platformColors = {
    youtube: "#FF0000",
    instagram: "#E1306C",
    tiktok: "#000000",
  };

  const togglePlatform = (platform) => {
    setSelectedPlatform((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    );
  };

  return (
    <div
      className="roster-page"
      style={{ width: "100vw", overflowY: "hidden", height: " calc(100vh)" }}
    >
      <div className="page_wrapper">
        <div className="mobile_header for_mobile">
          <div className="logo_part">
            <img
              src="/cm_logo.png"
              alt="Logo"
              style={{ width: "100%", maxwidth: "160px" }}
            />
          </div>
          <div className="hamburger_menu">
            <span className="hamburger" onClick={toggleSidebar}>
              <svg
                width="40px"
                height="40px"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 18H10"
                  stroke="#000000"
                  stroke-width="2"
                  stroke-linecap="round"
                />
                <path
                  d="M4 12L16 12"
                  stroke="#000000"
                  stroke-width="2"
                  stroke-linecap="round"
                />
                <path
                  d="M4 6L20 6"
                  stroke="#000000"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </span>
          </div>
        </div>
        <div className="sidebar_sec">
          <Sidebar stats={metaStats} handleOfferSubmit={handleOfferSubmit} />
        </div>

        <div className="main-content">
          <div className="search-export-container">
            <div className="top-row">
              <input
                type="text"
                className="search-input"
                placeholder="Search creators"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />

              <button
                className="csv-button for_mobile"
                onClick={() =>
                  handleExportCSV(
                    searchText,
                    selectedCategories,
                    minReach,
                    maxReach,
                    minUsa,
                    maxUsa,
                    selectedCountry,
                    selectedPlatform,
                    minFemale,
                    maxFemale,
                    minMale,
                    maxMale
                  )
                }
              >
                📄 <span class="csv-label">CSV</span>
              </button>
              <button
                className="csv-button for_desktop"
                onClick={() => {
                  document.body.classList.toggle("filter_menu_open");
                }}
              >
                <span className="filter-icon">≡</span> Filter
              </button>
            </div>

            <div className="bottom-row">
              <button
                class="filter-button for_mobile"
                onClick={() => {
                  document.body.classList.toggle("filter_menu_open");
                }}
              >
                <span class="filter-icon">≡</span> Filter
              </button>
              <button
                className="csv-button for_desktop"
                onClick={() =>
                  handleExportCSV(
                    searchText,
                    selectedCategories,
                    minReach,
                    maxReach,
                    minUsa,
                    maxUsa,
                    selectedCountry,
                    selectedPlatform,
                    minFemale,
                    maxFemale,
                    minMale,
                    maxMale
                  )
                }
              >
                📄 <span class="csv-label">CSV</span>
              </button>
            </div>
          </div>

          <div className="main-wrapper">
            {creators?.map((creator) => (
              <CreatorCard
                key={creator.id}
                creator={creator}
                handleOfferSubmit={handleOfferSubmit}
              />
            ))}
            <div className="footer_outer">
              {/* Footer */}
              <footer className="footer">
                <div className="container">
                  <p>© 2025 Creatives Matter LLC</p>
                  <div className="footer-links">
                    <Link to="/privacy-policy">Privacy Policy</Link>
                    <Link to="/terms-of-service">Terms of Service</Link>
                  </div>
                </div>
              </footer>
            </div>
          </div>
        </div>

        <div className="side_filter">
          <div className="filter_inner">
            <div className="filter_head">
              <h2>
                Filter
                <span
                  className="filter-close"
                  onClick={() =>
                    document.body.classList.remove("filter_menu_open")
                  }
                >
                  {" "}
                  <svg
                    width="25px"
                    height="25px"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M5.29289 5.29289C5.68342 4.90237 6.31658 4.90237 6.70711 5.29289L12 10.5858L17.2929 5.29289C17.6834 4.90237 18.3166 4.90237 18.7071 5.29289C19.0976 5.68342 19.0976 6.31658 18.7071 6.70711L13.4142 12L18.7071 17.2929C19.0976 17.6834 19.0976 18.3166 18.7071 18.7071C18.3166 19.0976 17.6834 19.0976 17.2929 18.7071L12 13.4142L6.70711 18.7071C6.31658 19.0976 5.68342 19.0976 5.29289 18.7071C4.90237 18.3166 4.90237 17.6834 5.29289 17.2929L10.5858 12L5.29289 6.70711C4.90237 6.31658 4.90237 5.68342 5.29289 5.29289Z"
                      fill="#0F1729"
                    />
                  </svg>
                </span>
              </h2>
            </div>
            <div className="filter_body">
              <div className="filter-group">
                <label>Country</label>
                <select
                  className="full-width custom_input"
                  name="country"
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                >
                  <option value="">Select a country</option>
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>

              <div class="filter-group">
                <label>Categories</label>
                <div class="categories">
                  <label>
                    <input
                      type="checkbox"
                      value="Art"
                      checked={selectedCategories.includes("Art")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Art
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="ASMR"
                      checked={selectedCategories.includes("ASMR")}
                      onChange={handleCategoryChange}
                    />{" "}
                    ASMR
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Automotive"
                      checked={selectedCategories.includes("Automotive")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Automotive
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Beauty"
                      checked={selectedCategories.includes("Beauty")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Beauty
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Choreography"
                      checked={selectedCategories.includes("Choreography")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Choreography
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Comedy"
                      checked={selectedCategories.includes("Comedy")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Comedy
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Community"
                      checked={selectedCategories.includes("Community")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Community
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Dance"
                      checked={selectedCategories.includes("Dance")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Dance
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="DIY"
                      checked={selectedCategories.includes("DIY")}
                      onChange={handleCategoryChange}
                    />{" "}
                    DIY
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Entertainment"
                      checked={selectedCategories.includes("Entertainment")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Entertainment
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Family"
                      checked={selectedCategories.includes("Family")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Family
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Fashion"
                      checked={selectedCategories.includes("Fashion")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Fashion
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Film & Television"
                      checked={selectedCategories.includes("Film & Television")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Film & Television
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Fitness"
                      checked={selectedCategories.includes("Fitness")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Fitness
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Food & Cooking"
                      checked={selectedCategories.includes("Food & Cooking")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Food & Cooking
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Gaming"
                      checked={selectedCategories.includes("Gaming")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Gaming
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Health & Wellness"
                      checked={selectedCategories.includes("Health & Wellness")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Health & Wellness
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Home"
                      checked={selectedCategories.includes("Home")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Home
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Humor & Comedy"
                      checked={selectedCategories.includes("Humor & Comedy")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Humor & Comedy
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Law"
                      checked={selectedCategories.includes("Law")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Law
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Lifestyle"
                      checked={selectedCategories.includes("Lifestyle")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Lifestyle
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      value="MentalHealth"
                      checked={selectedCategories.includes("MentalHealth")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Mental Health
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Modeling"
                      checked={selectedCategories.includes("Modeling")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Modeling
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Motivational"
                      checked={selectedCategories.includes("Motivational")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Motivational
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Music"
                      checked={selectedCategories.includes("Music")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Music
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      value="PersonalDevelopment"
                      checked={selectedCategories.includes(
                        "PersonalDevelopment"
                      )}
                      onChange={handleCategoryChange}
                    />{" "}
                    Personal Development
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="PetsAnimals"
                      checked={selectedCategories.includes("PetsAnimals")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Pets & Animals
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Plants"
                      checked={selectedCategories.includes("Plants")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Plants
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Podcast"
                      checked={selectedCategories.includes("Podcast")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Podcast
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="RealEstate"
                      checked={selectedCategories.includes("RealEstate")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Real Estate
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Relationships"
                      checked={selectedCategories.includes("Relationships")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Relationships
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      value="SocialMedia"
                      checked={selectedCategories.includes("SocialMedia")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Social Media
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      value="Sports"
                      checked={selectedCategories.includes("Sports")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Sports
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Travel"
                      checked={selectedCategories.includes("Travel")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Travel
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="YouTuber"
                      checked={selectedCategories.includes("YouTuber")}
                      onChange={handleCategoryChange}
                    />{" "}
                    YouTuber
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      value="Other"
                      checked={selectedCategories.includes("Other")}
                      onChange={handleCategoryChange}
                    />{" "}
                    Other
                  </label>
                </div>
              </div>

              <div className="filter-group">
                <label>Platform</label>
                <div
                  className="platform-buttons"
                  style={{ display: "flex", gap: "10px" }}
                >
                  {/* YouTube */}
                  <button
                    onClick={() => togglePlatform("youtube")}
                    style={{
                      backgroundColor: selectedPlatform.includes("youtube")
                        ? platformColors.youtube
                        : unselectedBg,
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px",
                      cursor: "pointer",
                    }}
                  >
                    <FaYoutube
                      style={{
                        fontSize: iconSize,
                        color: selectedPlatform.includes("youtube")
                          ? "#ffffff"
                          : unselectedColor,
                      }}
                    />
                  </button>

                  {/* Instagram */}
                  <button
                    onClick={() => togglePlatform("instagram")}
                    style={{
                      backgroundColor: selectedPlatform.includes("instagram")
                        ? platformColors.instagram
                        : unselectedBg,
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px",
                      cursor: "pointer",
                    }}
                  >
                    <FaInstagram
                      style={{
                        fontSize: iconSize,
                        color: selectedPlatform.includes("instagram")
                          ? "#ffffff"
                          : unselectedColor,
                      }}
                    />
                  </button>

                  {/* TikTok */}
                  <button
                    onClick={() => togglePlatform("tiktok")}
                    style={{
                      backgroundColor: selectedPlatform.includes("tiktok")
                        ? platformColors.tiktok
                        : unselectedBg,
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px",
                      cursor: "pointer",
                    }}
                  >
                    <FaTiktok
                      style={{
                        fontSize: iconSize,
                        color: selectedPlatform.includes("tiktok")
                          ? "#ffffff"
                          : unselectedColor,
                      }}
                    />
                  </button>
                </div>
              </div>

              <div class="filter-group">
                <label>Total Reach</label>
                <div class="range-inputs">
                  <input
                    type="number"
                    placeholder="Min"
                    className="custom_input"
                    value={minReach}
                    onChange={(e) => setMinReach(e.target.value)}
                  />
                  <span>-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    className="custom_input"
                    value={maxReach}
                    onChange={(e) => setMaxReach(e.target.value)}
                  />
                </div>
              </div>

              <div class="filter-group">
                <label>USA Audience%</label>
                <div class="range-inputs">
                  <input
                    type="number"
                    placeholder="Min"
                    className="custom_input"
                    value={minUsa}
                    onChange={(e) => setMinUsa(e.target.value)}
                  />
                  <span>-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    className="custom_input"
                    value={maxUsa}
                    onChange={(e) => setMaxUsa(e.target.value)}
                  />
                </div>
              </div>

              <div class="filter-group">
                <label>Female Audience%</label>
                <div class="range-inputs">
                  <input
                    type="text"
                    placeholder="Min"
                    className="custom_input"
                    value={minFemale}
                    onChange={(e) => setminFemale(e.target.value)}
                  />
                  <span>-</span>
                  <input
                    type="text"
                    placeholder="Max"
                    className="custom_input"
                    value={maxFemale}
                    onChange={(e) => setmaxFemale(e.target.value)}
                  />
                </div>
              </div>

              <div class="filter-group">
                <label>Male Audience%</label>
                <div class="range-inputs">
                  <input
                    type="text"
                    placeholder="Min"
                    className="custom_input"
                    value={minMale}
                    onChange={(e) => setminMale(e.target.value)}
                  />
                  <span>-</span>
                  <input
                    type="text"
                    placeholder="Max"
                    className="custom_input"
                    value={maxMale}
                    onChange={(e) => setmaxMale(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div class="filter-footer">
              <button
                className="clear-btn"
                onClick={() => {
                  setSelectedCategories([]);
                  setMinReach("");
                  setMaxReach("");
                  setMinUsa("");
                  setMaxUsa("");
                  setminFemale("");
                  setmaxFemale("");
                  setminMale("");
                  setmaxMale("");
                  setSearchText("");
                  setSelectedCountry("");
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatorList;
